import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import FilterChip from "@/components/Common/filter/FilterChip";
import map from "lodash/map";
import omit from "lodash/omit";
import {useLayoutEffect, useRef, useState, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {useClickAway, useKeyPressEvent} from "react-use";

const SortButton = <T extends string>({
  ClosedIconComponent,
  OpenedIconComponent,
  translations,
  ordering,
  setOrdering,
  placeholder,
}: {
  ClosedIconComponent?: ReactNode;
  OpenedIconComponent?: ReactNode;
  translations: Record<T, string>;
  ordering: T | null;
  setOrdering: (ordering: T | null) => void;
  placeholder?: string;
}): ReactNode => {
  const {t} = useTranslation("common");
  const placeholderText = placeholder ?? t("sortBy");
  const ref = useRef<HTMLDivElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  useClickAway(divRef, () => setIsOpen(false));
  useKeyPressEvent("Escape", () => setIsOpen(false));
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [buttonWidth, setButtonWidth] = useState<number>(0);
  const [buttonHeight, setButtonHeight] = useState<number>(0);

  useLayoutEffect(() => {
    setButtonWidth(Math.max(ref.current?.getBoundingClientRect().width ?? 0, 0));
    setButtonHeight(ref.current?.getBoundingClientRect().height ?? 0);
  }, [isOpen]);

  return (
    <div className="relative w-fit divide-gray-100 rounded-sm">
      <div
        ref={ref}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="border-neutral-grey-light-dark flex h-10 cursor-pointer flex-row items-center justify-between gap-x-3 rounded-sm border bg-white px-3 py-1">
        <div className="flex items-center">
          {ClosedIconComponent}
          {!ordering ? (
            <span className="text-neutral-black ml-2 text-sm font-semibold whitespace-nowrap">
              {translations[ordering!] || placeholderText}
            </span>
          ) : (
            <FilterChip
              className="ml-2"
              label={translations[ordering!]!}
              onRemove={e => {
                e.stopPropagation();
                setOrdering(null);
              }}
            />
          )}
        </div>
        <RightChevronIcon className="text-neutral-black h-3 rotate-90" />
      </div>
      {isOpen && (
        <div
          ref={divRef}
          onClick={e => {
            e.stopPropagation();
            setIsOpen(false);
          }}
          style={{
            transform: `translateY(${-buttonHeight}px)`,
          }}
          className="absolute right-0 z-1000 rounded-sm">
          <div
            style={{width: buttonWidth}}
            className="border-primary-dark-solid text-primary-dark-solid mb-4 ml-auto flex h-10 cursor-pointer flex-row items-center justify-between gap-x-3 truncate rounded-sm border bg-white px-3 py-1">
            <div className="flex flex-row items-center">
              {OpenedIconComponent}
              {!ordering ? (
                <span className="ml-2 text-sm font-semibold whitespace-nowrap">{placeholderText}</span>
              ) : (
                <FilterChip
                  className="ml-2"
                  label={translations[ordering!]!}
                  onRemove={e => {
                    e.stopPropagation();
                    setOrdering(null);
                    setIsOpen(false);
                  }}
                />
              )}
            </div>
            <RightChevronIcon className="text-primary-dark-solid h-3 -rotate-90" />
          </div>
          <div className="w-60 rounded-sm bg-white pl-0 shadow-lg">
            <ul className="flex w-full cursor-pointer flex-col divide-y pl-0 text-center align-middle">
              {map(omit(translations, [ordering!]), (value, key) => (
                <li
                  key={key}
                  onClick={e => {
                    e.stopPropagation();
                    setOrdering(key as T);
                    setIsOpen(false);
                  }}
                  className="hover:bg-muted py-2 text-sm first:rounded-t-sm last:rounded-b-sm">
                  {value}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default SortButton;
